/**
 * Derives community context (`community_id` / `community_space_id`) stamped onto
 * Nectar pageview/click events on community routes, mirroring how `postId` is
 * read from route loaders in `components/App/index.tsx`.
 *
 * Sendable (`sendable_id`/`sendable_type`) is intentionally NOT derived here yet —
 * that lands later with specific community events (e.g. community_post_liked).
 */
export interface CommunityAnalyticsContext {
  communityId?: string;
  communitySpaceId?: string;
}

interface DeriveCommunityAnalyticsContextInput {
  pathname: string;
  /** `community` from the `routes/community` layout loader. */
  community?: { id: string } | null;
  /** `spaces` from the `routes/community` layout loader. */
  spaces?: Array<{ id: string; slug: string }>;
  /** `communityPost` from the `routes/community/posts/$id` loader, when on a post page. */
  post?: { space?: { id?: string } | null } | null;
}

const SPACE_SLUG_PATTERN = /\/community\/channels\/([^/?#]+)/;

const resolveSpaceId = ({ pathname, spaces, post }: DeriveCommunityAnalyticsContextInput): string | undefined => {
  // A community post carries its own space; prefer it on post pages.
  if (post?.space?.id) return post.space.id;

  // Otherwise, on a space route resolve the space id from the layout's spaces by slug.
  const slug = pathname.match(SPACE_SLUG_PATTERN)?.[1];
  if (!slug) return undefined;
  return spaces?.find((space) => space.slug === slug)?.id;
};

export const deriveCommunityAnalyticsContext = (
  input: DeriveCommunityAnalyticsContextInput,
): CommunityAnalyticsContext => {
  // No layout community means we're not on a community route.
  if (!input.community) return {};

  return {
    communityId: input.community.id,
    communitySpaceId: resolveSpaceId(input),
  };
};
